import type { ReactNode } from 'react';
import { useDeviceStore } from '../store/device';

/**
 * Chrome and other browsers block audio playback (incoming-call ringtone,
 * remote-party audio) until the page has received a user gesture. We surface
 * an explicit "Enable audio" button on first dashboard load so the user
 * primes the audio context before any call comes in.
 */
export function AudioGate({ children }: { children: ReactNode }) {
  const audioUnlocked = useDeviceStore(s => s.audioUnlocked);
  const unlockAudio = useDeviceStore(s => s.unlockAudio);
  const device = useDeviceStore(s => s.device);

  if (audioUnlocked) return <>{children}</>;

  function unlock() {
    if (device?.audio) {
      // Pre-warm the audio output context. The user gesture is the click itself.
      device.audio.unsetAudioConstraints?.();
    }
    // Try to play a silent buffer to satisfy autoplay policies.
    try {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        if (ctx.state === 'suspended') void ctx.resume();
      }
    } catch (err) {
      console.warn('audio context resume failed', err);
    }
    unlockAudio();
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
      <h3 className="text-sm font-semibold text-amber-900">Enable audio to receive calls</h3>
      <p className="text-xs text-amber-800 mt-1">
        Your browser blocks audio playback until you interact with the page. Click below before going Available.
      </p>
      <button
        onClick={unlock}
        className="mt-3 rounded-lg bg-amber-600 text-white px-4 py-2 text-sm font-medium hover:bg-amber-700"
      >
        Enable audio
      </button>
    </div>
  );
}
